import React from "react"
import { observer } from "mobx-react-lite"
import { useAlert } from "react-alert"

import { useSearchBlock } from "@pages/search/SearchContext/SearchResultContext"
import SearchSummaryBlockStore from "@store/search/search-summary-block.store"
import { useController, useStore } from "@store/index"
import { ModalsTypes } from "@components/modals/constants"
import useModal from "@components/modals/useModal"
import Button from "@components/ui/Button/Button"
import Icon from "@components/ui/Icon/Icon"
import Text from "@components/ui/Typography/Text"
import { useSolution } from "@pages/launch-solutions/ActiveSolutionContext"
import Expert from "@pages/search/SearchFlow/Expert"

import styles from "./HelpfulFeedbackWidget.module.sass"

const SUCCESS_MESSAGE_TITLE = "Thanks for your feedback"
const SUCCESS_MESSAGE_SUBTITLE = "It helps us improve the answers."

/**
 * LAB-47: replaces the 1-5 star rating with a plain helpful / unhelpful choice.
 *
 * The stars were dropped because they were not proving useful as a signal, but
 * the score they produced is load-bearing: it drives the dashboard average, the
 * time-saved metric and the Feedback Rating column in the BU export. So the
 * thumbs are stored on the same 1-5 field - helpful writes 5, unhelpful writes 1
 * - which keeps every downstream consumer working untouched. Expect the average
 * rating trend to step at the point this ships, since scores become all-or-
 * nothing rather than clustered around the middle.
 */
const HELPFUL_RATING = 5
const UNHELPFUL_RATING = 1

type Props = {
  className?: string
  disabled?: boolean
  message?: string
}

const HelpfulFeedbackWidget: React.FC<Props> = observer(
  ({ disabled = false }) => {
    const alert = useAlert()
    const modal = useModal(ModalsTypes.ANSWER_FEEDBACK_MODAL)

    const { solution } = useSolution()

    const { searchEntityBlock, searchEntity } = useSearchBlock()
    const { factFinderSolutionController } = useController()

    const { restrictionsStore: access } = useStore()

    if (!(searchEntityBlock instanceof SearchSummaryBlockStore))
      throw new Error(
        "This answers data store does not support voting feature yet"
      )

    // Set once the user says the answer was unhelpful, so the escalation prompt
    // is offered exactly when the story asks for it.
    const [showExpertPrompt, setShowExpertPrompt] = React.useState(false)

    const submitted = disabled || !!searchEntity.feedback

    const submitVote = async (rating: number, comment: string) => {
      const rawAnswer = searchEntityBlock.serialize()
      if (rawAnswer == null) return

      const error = await factFinderSolutionController.voteForSearchSummary(
        searchEntity,
        searchEntity.filter.searchQuery,
        rawAnswer,
        comment,
        searchEntityBlock.queryType,
        solution.id,
        rating
      )

      if (error) {
        alert.error(error)
        return
      }

      alert.success(
        <>
          {SUCCESS_MESSAGE_TITLE}
          <Text variant="h5" color="text50Color">
            {SUCCESS_MESSAGE_SUBTITLE}
          </Text>
        </>
      )
    }

    const handleHelpful = () => submitVote(HELPFUL_RATING, "")

    const handleUnhelpful = () => {
      // Keep capturing the free-text comment: it is its own column in the BU
      // export and is the only qualitative signal left once the stars are gone.
      modal.showModal({
        rating: UNHELPFUL_RATING,
        onSubmit: async (rating: number, comment: string) => {
          await submitVote(rating, comment)
          setShowExpertPrompt(true)
          modal.hideModal()
        },
      })
    }

    return (
      <div className={styles.root}>
        <div className={styles.prompt}>
          <span className={styles.question}>Was this answer helpful?</span>

          <Button
            size="small"
            variant="outlined"
            color="default"
            disabled={submitted}
            before={<Icon name="thumbs-up" />}
            onClick={handleHelpful}
          >
            Helpful
          </Button>

          <Button
            size="small"
            variant="outlined"
            color="default"
            disabled={submitted}
            before={<Icon name="thumbs-down" />}
            onClick={handleUnhelpful}
          >
            Unhelpful
          </Button>
        </div>

        {showExpertPrompt && access.canPostToExpert && (
          <div className={styles.expertPrompt}>
            <Text variant="caption2">Didn&apos;t get the answer you need?</Text>
            <Expert color="primary" variant="outlined" />
          </div>
        )}
      </div>
    )
  }
)

export default HelpfulFeedbackWidget
